import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, Lock, Save, Loader2 } from 'lucide-react';
import OpsModal from '@/components/ops/OpsModal';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

/**
 * Notification preferences modal — Module 6 Pass C.
 * Lists all 12 event types with two checkboxes per row (In-app / Email).
 * Security events show as "Always on (security)" and are non-editable.
 *
 * Props:
 *  - targetUserId: optional. When provided, admin manages another user's prefs.
 *  - targetUserName: optional display name for header.
 *  - onClose
 */
export default function NotificationPreferencesModal({ onClose, targetUserId, targetUserName }) {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const getUrl = targetUserId
    ? `${API}/ops/users/${targetUserId}/notification-preferences`
    : `${API}/ops/notifications/preferences`;
  const putUrl = getUrl;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(getUrl, getAuth());
        if (alive) setPrefs(data || []);
      } catch (e) {
        toast.error('Could not load notification preferences');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [getUrl]);

  const toggle = (event_type, channel) => {
    setPrefs(p => p.map(row => {
      if (row.event_type !== event_type) return row;
      if (row.security) return row;
      return { ...row, [channel]: !row[channel] };
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = prefs
        .filter(p => !p.security)
        .reduce((acc, p) => {
          acc[p.event_type] = { in_app: !!p.in_app, email: !!p.email };
          return acc;
        }, {});
      await axios.put(putUrl, payload, getAuth());
      toast.success('Notification preferences saved');
      setDirty(false);
      onClose?.();
    } catch (e) {
      toast.error('Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OpsModal
      title={targetUserId ? `Notification preferences — ${targetUserName || 'user'}` : 'Notification preferences'}
      subtitle={targetUserId ? 'Admin override. Changes affect this user only.' : 'Choose how you receive notifications across in-app and email.'}
      icon={Bell}
      onClose={onClose}
      size="lg"
      zClass="z-[60]"
      testid="notification-preferences-modal"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="notif-prefs-cancel"
            className="h-8 px-3 text-[12px] text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="notif-prefs-save"
            className="h-8 px-3 text-[12px] font-medium text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md inline-flex items-center gap-1.5 transition-colors"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save preferences
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="py-10 grid place-items-center">
          <Loader2 size={20} className="text-slate-400 animate-spin" />
        </div>
      ) : (
        <div className="bg-white">
          <div className="grid grid-cols-[1fr_80px_80px] gap-2 px-3 py-2 border-b border-slate-200 text-[10px] uppercase tracking-wider font-medium text-slate-400">
            <span>Event</span>
            <span className="text-center">In-app</span>
            <span className="text-center">Email</span>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="notif-prefs-list">
            {prefs.map(row => (
              <li
                key={row.event_type}
                className="grid grid-cols-[1fr_80px_80px] gap-2 px-3 py-2.5 items-center hover:bg-slate-50/60"
                data-testid={`notif-pref-row-${row.event_type}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] text-slate-900 truncate leading-tight">{row.label}</p>
                  {row.description && (
                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{row.description}</p>
                  )}
                </div>
                {row.security ? (
                  <span className="col-span-2 text-[11px] text-slate-500 inline-flex items-center gap-1 justify-end pr-1">
                    <Lock size={10} className="text-slate-400" /> Always on (security)
                  </span>
                ) : (
                  <>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={!!row.in_app}
                        onChange={() => toggle(row.event_type, 'in_app')}
                        data-testid={`notif-pref-${row.event_type}-in_app`}
                        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-1 focus:ring-slate-900 focus:ring-offset-0 cursor-pointer accent-slate-900"
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={!!row.email}
                        onChange={() => toggle(row.event_type, 'email')}
                        data-testid={`notif-pref-${row.event_type}-email`}
                        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-1 focus:ring-slate-900 focus:ring-offset-0 cursor-pointer accent-slate-900"
                      />
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </OpsModal>
  );
}
